import { MatDialog } from '@angular/material/dialog';
import { MaterialExportInfoDto } from './../../../../data-transfer/entities/material-export-info-dto';
import { ComponentExportInfoDto } from './../../../../data-transfer/entities/component-export-info-dto';
import { Component, OnInit } from '@angular/core';
import { MatTabChangeEvent } from '@angular/material/tabs';
import { toSignal } from '@angular/core/rxjs-interop';
import { ExportInfoDto } from 'src/app/data-transfer/entities/export-info-dto';
import { UnitExportInfoDto } from 'src/app/data-transfer/entities/unit-export-info-dto';
import { SystemExportInfoDto } from 'src/app/data-transfer/entities/system-export-info-dto';
import { PackagingPart } from 'src/app/model/packaging-part-enum';
import { ImportExportApiService } from 'src/app/data-transfer/services/import-export-api-service';
import { getDialogConfig } from 'src/app/util/dialog-util';
import { SimpleConfirmDialogComponent } from 'src/app/components/dialogs/simple-confirm-dialog/simple-confirm-dialog.component';
import { DialogActions } from 'src/app/model/dictionary';
import { TranslocoService } from '@jsverse/transloco';
import { SimpleAlertDialogComponent, SimpleDialogData } from 'src/app/components/dialogs/simple-alert-dialog/simple-alert-dialog.component';
import { getDatasetLockedDialogData } from 'src/app/util/dataset-lock-util';
import { PaginationDataSource } from 'src/app/util/pagination-datasource';
import { OrganizationDto } from 'src/app/data-transfer/entities/organization-dto';
import { PAGE_SIZE_OPTIONS } from 'src/app/model/pagination-defaults';
import { ExportInfoPaginationFields } from 'src/app/data-transfer/entities/pagination-fields';

@Component({
  selector: 'app-export-list',
  templateUrl: './export-list.component.html',
  styleUrls: ['./export-list.component.scss'],
})
export class ExportListComponent implements OnInit {
  packagingPartEnum = PackagingPart;

  protected readonly pageSizeOptions = PAGE_SIZE_OPTIONS.DEFAULT;
  protected readonly defaultPageSize = this.pageSizeOptions[0];

  // data for packaging systems
  systemDisplayedColumns: string[] = [
    'organization',
    'packagingId',
    'productName',
    'version',
    'status',
    'tracking',
    'exportTimestamp',
    'exportProfile',
    'action',
  ];
  readonly systemDataSource = new PaginationDataSource<SystemExportInfoDto>(query =>
    this.importExportApiService.getExportListByPage(PackagingPart.System, query)
  );
  readonly $organizations = toSignal(
    this.importExportApiService.getExportOrganizations(PackagingPart.System),
    { initialValue: [] as OrganizationDto[] }
  );

  // data for packaging units
  unitDisplayedColumns: string[] = [
    'organization',
    'packagingId',
    'productName',
    'version',
    'status',
    'tracking',
    'exportTimestamp',
    'exportProfile',
    'action',
  ];
  readonly unitDataSource = new PaginationDataSource<UnitExportInfoDto>(query =>
    this.importExportApiService.getExportListByPage(PackagingPart.Unit, query)
  );
  readonly $unitOrganizations = toSignal(
    this.importExportApiService.getExportOrganizations(PackagingPart.Unit),
    { initialValue: [] as OrganizationDto[] }
  );

  // data for packaging components
  componentDisplayedColumns: string[] = [
    'organization',
    'packagingId',
    'articleName',
    'version',
    'status',
    'tracking',
    'exportTimestamp',
    'exportProfile',
    'action',
  ];
  readonly componentDataSource = new PaginationDataSource<ComponentExportInfoDto>(query =>
    this.importExportApiService.getExportListByPage(PackagingPart.Component, query)
  );
  readonly $componentOrganizations = toSignal(
    this.importExportApiService.getExportOrganizations(PackagingPart.Component),
    { initialValue: [] as OrganizationDto[] }
  );

  // data for packaging materials
  materialDisplayedColumns: string[] = [
    'organization',
    'packagingId',
    'articleName',
    'version',
    'status',
    'tracking',
    'exportTimestamp',
    'exportProfile',
    'action',
  ];
  readonly materialDataSource = new PaginationDataSource<MaterialExportInfoDto>(query =>
    this.importExportApiService.getExportListByPage(PackagingPart.Material, query)
  );
  readonly $materialOrganizations = toSignal(
    this.importExportApiService.getExportOrganizations(PackagingPart.Material),
    { initialValue: [] as OrganizationDto[] }
  );

  // Tracks which tabs already had their data fetched
  private loadedTabIndices: number[] = [];

  constructor(
    private dialog: MatDialog,
    private importExportApiService: ImportExportApiService,
    private translateService: TranslocoService
  ) { }

  ngOnInit(): void {
    this.onTabChanged({ index: 0 } as MatTabChangeEvent);
  }

  onTabChanged(event: MatTabChangeEvent): void {
    if (this.loadedTabIndices.includes(event.index)) {
      return;
    }
    this.loadedTabIndices.push(event.index);

    switch (event.index) {
      case 0:
        this.systemDataSource.fetchData(0, this.defaultPageSize, undefined, ExportInfoPaginationFields.Id);
        break;
      case 1:
        this.unitDataSource.fetchData(0, this.defaultPageSize, undefined, ExportInfoPaginationFields.Id);
        break;
      case 2:
        this.componentDataSource.fetchData(0, this.defaultPageSize, undefined, ExportInfoPaginationFields.Id);
        break;
      case 3:
        this.materialDataSource.fetchData(0, this.defaultPageSize, undefined, ExportInfoPaginationFields.Id);
        break;
    }
  }

  changeTracking(exportInfo: ExportInfoDto<any, any>, packagingPart: PackagingPart) {
    const dialogConfig = getDialogConfig(this.getChangeTrackingConfirmationDialogData());
    const dialogRef = this.dialog.open(SimpleConfirmDialogComponent, dialogConfig);
    dialogRef.afterClosed().subscribe(dialogResult => {
      if (dialogResult.event === DialogActions.REJECT) {
        return;
      }

      this.importExportApiService.setTrackable(exportInfo.importCandidateId, !exportInfo.tracking, packagingPart).subscribe(() => {
        this.reloadExportList(packagingPart);
      });
    });
  }

  deleteImport(exportInfo: ExportInfoDto<any, any>, packagingPart: PackagingPart) {
    const dialogConfig = getDialogConfig(this.getDeleteConfirmationDialogData());
    const dialogRef = this.dialog.open(SimpleConfirmDialogComponent, dialogConfig);
    dialogRef.afterClosed().subscribe(dialogResult => {
      if (dialogResult.event === DialogActions.REJECT) {
        return;
      }

      this.importExportApiService.deleteImportCandidate(exportInfo.importCandidateId, packagingPart).subscribe({
        next: () => this.reloadExportList(packagingPart),
        error: error => this.dialog.open(
          SimpleAlertDialogComponent, getDialogConfig(getDatasetLockedDialogData(error, this.translateService), '350px'))
      });
    });
  }

  reloadExportList(packagingPart: PackagingPart) {
    switch (packagingPart) {
      case PackagingPart.System: this.systemDataSource.refresh(); break;
      case PackagingPart.Unit: this.unitDataSource.refresh(); break;
      case PackagingPart.Component: this.componentDataSource.refresh(); break;
      case PackagingPart.Material: this.materialDataSource.refresh(); break;
    }
  }

  private getDeleteConfirmationDialogData(): SimpleDialogData {
    return {
      title: this.translateService.translate('common.text.information'),
      messages: [this.translateService.translate('dataManagement.exportList.dialog.delete')],
      icon: 'info',
    };
  }

  private getChangeTrackingConfirmationDialogData(): SimpleDialogData {
    return {
      title: this.translateService.translate('common.text.information'),
      messages: [this.translateService.translate('dataManagement.exportList.dialog.changeTracking')],
      icon: 'info',
    };
  }

}
